import { message } from 'antd'
import { useEffect, useState } from 'react'

import ClearIcon from '@mui/icons-material/Clear'
import SaveIcon from '@mui/icons-material/Save'
import store from 'store2'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ProductSelectSearch from '../../../../../components/SelectSearch/ProductSelectSearch'
import { IProduct } from '../../../../../types/product'
import { useDispatch, useSelector } from 'react-redux'
import { setProductSearchData } from '../../../../../../store/reducers/search/search.slice'
import { LAST_PRODUCT_STORE } from '../../../../../constants/global'
import { isEmpty } from 'radash'
import { Tag } from '../../../../../components/Tag'
import { uniq, uniqBy } from 'lodash'

interface OrderFormAddProductModalProps {
  onAdd: (value: OrderFormAddProductModal & { product: IProduct }) => void
  isOpen: boolean
  onClose: (visible: boolean) => void
  setSearchProductName?: (value: string) => void
  searchProductName?: string
}

export interface OrderFormAddProductModal {
  quantity: number
  discount: number
  total?: number
  size?: string
  isFree?: string
}

const getAddProductFormValidationSchema = () => {
  return Yup.object({
    quantity: Yup.number().required('กรุณากรอกจำนวนสินค้า'),
    discount: Yup.number().required('กรุณากรอกส่วนลด'),
  })
}

export const OrderFormAddProductModal = (
  props: OrderFormAddProductModalProps,
) => {
  const {
    onAdd,
    isOpen,
    onClose,
    setSearchProductName,
    searchProductName = '',
  } = props
  const lastProductSelected = store.get(LAST_PRODUCT_STORE)

  const [lastSearchProduct, setLastSearchProduct] = useState<IProduct[] | null>(
    lastProductSelected,
  )
  const [searchProduct, setSearchProduct] = useState<string>()
  const [product, setProduct] = useState<IProduct>()
  const formik = useFormik({
    initialValues: {
      quantity: 0,
      discount: 0,
      total: 0,
      size: '',
      isFree: 'no',
    },
    validationSchema: getAddProductFormValidationSchema(),
    onSubmit: async (values: OrderFormAddProductModal) => {
      if (!product) {
        message.error('กรุณาเลือกสินค้า')
        return
      }
      const isFree = values.isFree === 'yes'
      onAdd({
        product: {
          ...product,
          price: isFree ? 0 : product.price,
        } as IProduct,
        quantity: values.quantity,
        discount: isFree ? 0 : values.discount,
        size: values.size,
        isFree: values.isFree,
      })
      onClose(false)
    },
  })

  const dispatch = useDispatch()
  const prevProduct: IProduct = useSelector(
    (state: any) => state.search.product,
  )

  useEffect(() => {
    if (prevProduct) {
      setProduct(prevProduct)
    }
  }, [prevProduct])

  const onSelectProduct = (product: IProduct) => {
    setProduct(product)
    dispatch(setProductSearchData({ product }))
    const discount = 0
    formik.setFieldValue('quantity', 1)
    formik.setFieldValue('discount', discount)
    formik.setFieldValue('size', '')
    formik.setFieldValue('total', product.price - discount)
    formik.setFieldValue('isFree', 'no')

    const products = store.get(LAST_PRODUCT_STORE) || []
    let lists = []
    if (!products || products.length < 10) {
      lists = [product, ...products]
    } else {
      lists = [product, ...products.slice(0, 9)]
    }
    lists = uniqBy(lists, '_id')
    store.set(LAST_PRODUCT_STORE, lists)
    setLastSearchProduct(lists)
    setSearchProduct?.(`${product.name} (${product.code})`)
  }

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="lg">
      <DialogTitle>เพิ่มสินค้า</DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            {!isEmpty(lastSearchProduct) && (
              <Grid item xs={12} lg={12}>
                <Typography fontSize={14} fontWeight={600} mb={0.5}>
                  สินค้าที่คุณเลือกล่าสุด 10 รายการ **(คลิกเพื่อเลือก)**
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {lastSearchProduct?.map((p) => (
                    <Tag
                      onClick={() => onSelectProduct(p)}
                      color={
                        p.objectId === product?.objectId ? 'error' : 'success'
                      }
                      sx={{ cursor: 'pointer' }}
                    >
                      {p.name} ({p.code})
                    </Tag>
                  ))}
                </Box>
              </Grid>
            )}
            <Grid item xs={12} lg={12}>
              <ProductSelectSearch
                value={searchProduct ? searchProduct : ''}
                multiple={false}
                onSelect={(e) => e && onSelectProduct(e?.value!)}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                disabled={!!!product}
                id="quantity"
                fullWidth
                label="กรอกจำนวนสินค้า"
                name="quantity"
                helperText={
                  formik.touched.quantity && (formik.errors.quantity as string)
                }
                error={!!(formik.touched.quantity && formik.errors.quantity)}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  const quantity = Number(e.target.value) || 0
                  formik.setFieldValue('quantity', quantity)
                  const total =
                    product?.price! * quantity - formik.values.discount
                  formik.setFieldValue('total', total)
                  const discount = Math.max(
                    0,
                    product?.price! * quantity - total,
                  )
                  formik.setFieldValue('discount', discount)
                }}
                value={formik.values.quantity}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                disabled={!!!product}
                id="discount"
                fullWidth
                label="กรอกส่วนลด"
                name="discount"
                helperText={
                  formik.touched.discount && (formik.errors.discount as string)
                }
                error={!!(formik.touched.discount && formik.errors.discount)}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  const discount = Math.max(0, Number(e.target.value) || 0)
                  formik.setFieldValue('discount', discount)
                  const total = Math.max(
                    0,
                    product?.price! * formik.values.quantity - discount,
                  )
                  formik.setFieldValue('total', total)
                }}
                value={formik.values.discount}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                disabled={!!!product}
                id="size"
                fullWidth
                label="กรอกขนาด"
                name="size"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.size}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                disabled={!!!product}
                id="total"
                fullWidth
                label="ราคารวม"
                name="total"
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  const total = e.target.value
                  formik.setFieldValue('total', total)
                  const discount = Math.max(
                    0,
                    product?.price! * formik.values.quantity - Number(total),
                  )
                  formik.setFieldValue('discount', discount)
                }}
                value={formik.values.total}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                id="isFree"
                disabled={!!!product}
                fullWidth
                label="สินค้าฟรี"
                name="isFree"
                select
                onChange={(e) => formik.setFieldValue('isFree', e.target.value)}
                value={formik.values.isFree || 'no'}
              >
                <MenuItem value="no">ไม่ใช่</MenuItem>
                <MenuItem value="yes">ใช่</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                disabled={!!!product}
                startIcon={<SaveIcon />}
                color="primary"
                type="submit"
                size="large"
                variant="contained"
              >
                เพิ่มสินค้า
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
